import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Copy, Eye, EyeOff, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { regenerateMerchantApiKey } from '../../lib/api';

const MerchantIntegrationTab = ({ profile, setProfile }) => {
  const [showApiKey, setShowApiKey] = useState(false);

  const handleRegenerateKey = async () => {
    try {
      const res = await regenerateMerchantApiKey();
      setProfile({ ...profile, api_key: res.data.api_key });
      toast.success('API key regenerated');
    } catch (error) {
      toast.error('Failed to regenerate key');
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  return (
    <div className="space-y-6 mt-6">
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">API Credentials</CardTitle>
          <CardDescription>Use these credentials to integrate ParachPay with your application</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Merchant ID</Label>
            <div className="flex gap-2">
              <Input value={profile.merchant_id} readOnly className="font-mono" data-testid="merchant-id-input" />
              <Button
                variant="outline"
                size="icon"
                onClick={() => copyToClipboard(profile.merchant_id, 'Merchant ID')}
                data-testid="copy-merchant-id-btn"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>API Key</Label>
            <div className="flex gap-2">
              <Input 
                type={showApiKey ? 'text' : 'password'}
                value={profile.api_key} 
                readOnly 
                className="font-mono"
                data-testid="api-key-input"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => setShowApiKey(!showApiKey)}
                data-testid="toggle-api-key-btn"
              >
                {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => copyToClipboard(profile.api_key, 'API Key')}
                data-testid="copy-api-key-btn"
              >
                <Copy className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={handleRegenerateKey}
                data-testid="regenerate-api-key-btn"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div className="mt-6 p-4 rounded-xl bg-muted">
            <h4 className="font-medium mb-2">Quick Integration Example</h4>
            <pre className="text-xs overflow-x-auto bg-background p-3 rounded-lg">
{`<script src="https://parachpay.com/widget.js"></script>
<div class="parachpay-button" 
     data-merchant="${profile.merchant_id}"
     data-amount="9.99"
     data-currency="USD">
</div>`}
            </pre>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MerchantIntegrationTab;
